import { FormControl } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BrandEnum } from "../../_common/enum/brand.enum";
import { FormTemplateTypeEnum } from "../../_common/enum/forms/fomTemplateType.enum";
import { IQuoteTrackingDetail } from "../../_common/interface/quoteTrackingDetail/iQuoteTrackingDetail.interface";
import config from "../../_config/config.const";
import { handleErrorResponse } from "../../_helpers/handleResponse.helper";
import { sendBodyHeightToParent, sendHeightToParent } from "../../_helpers/iFrameSize.helper";
import { formService } from "../../_services/form.service";
import { quoteTracking } from "../../_services/quoteTracking.service";

export function Forms(params: {brand: BrandEnum}) {
    const navigate = useNavigate();

    const resetTrackingData = (): IQuoteTrackingDetail => ({
        formId: null,
        formTypeId: "",
        quoteId: "",
        source: "",
        originalSource: "",
        medium: "",
        originalMedium: "",
        campaign: "",
        originalCampaign: "",
        keyword: "",
        originalKeyword: "",
        ip: "",
        trafficSource: "",
        originalReferringUrl: "",
        handlLandingPage: "",
        handlLandingPageBase: "",
        handlRef: "",
        handlOriginalRef: "",
        handlRefDomain: "",
        handlUrl: "",
        handlUrlBase: "",
        handlID: "",
        gClId: "",
        metaFbc: "",
        metaFbp: "",
        metaFbclid: ""
    });

    const [isLoading, setLoading] = useState(false);
    const [formTypeId, setFormTypeId] = useState<string | null>(null);
    const [trackingData, setTrackingData] = useState<IQuoteTrackingDetail>(resetTrackingData);

    const createdRef = useRef(false);
    const queryParamsRef = useRef<URLSearchParams>(new URLSearchParams(""));
    const pathnameRef = useRef<string>("");

    function normalizeOrigin(o: string) {
        return o ? o.replace(/\/+$/, "") : o;
    }
    function getAllowedOrigins(): string[] {
        const raw: unknown = (config as any).ORIGINS ?? (config as any).ORIGIN;
        if (Array.isArray(raw)) return raw.map(String).map(normalizeOrigin).filter(Boolean);
        if (typeof raw === "string" && raw.trim()) return [normalizeOrigin(raw)];
        return []; // no explicit origins configured
    }
    const parentOriginsRef = useRef<string[]>(getAllowedOrigins());

    // stability trackers
    const lastSerializedRef = useRef<string>("");
    const stableReadsRef = useRef<number>(0);

    const COOKIE_PARAM_KEYS = [
        "utm_source", "first_utm_source", "utm_medium", "first_utm_medium",
        "utm_campaign", "first_utm_campaign", "utm_term", "first_utm_term",
        "gclid", "handl_landing_page", "handl_landing_page_base", "handl_ref",
        "handl_original_ref", "handl_ref_domain", "handl_url", "handl_url_base",
        "handl_ip", "handlID", "traffic_source"
    ];

    //The iframe gets the query string more than once. First it might only have a few params. Later WordPress adds more(from cookies).
    //mergeParams keeps the old ones and adds any new ones
    function mergeParams(intoParams: URLSearchParams, incomingParams: URLSearchParams) {
        incomingParams.forEach((value, key) => {
            if (value && value !== "null" && value !== "undefined") intoParams.set(key, value);
        });
        return intoParams;
    }

    function getFormTypeFromPath(path: string): string | null {
        if (!path) return null;
        const p = path.toLowerCase();
        if (p.includes("life-insurance")) return FormTemplateTypeEnum.LifeInsurance.toString();
        if (p.includes("mortgage-protection")) return FormTemplateTypeEnum.MortgageProtection.toString();
        if (p.includes("income-protection")) return FormTemplateTypeEnum.IncomeProtection.toString();
        if (p.includes("illness")) return FormTemplateTypeEnum.SeriousIllnessProtection.toString();
        if (p.includes("mortgage") && !p.includes("protection")) return FormTemplateTypeEnum.Mortgages.toString();
        if (p.includes("health-application")) return FormTemplateTypeEnum.HealthQuestionnaire.toString();
        return null;
    }

    function isCompletedPage(path: string) {
        return path.toLowerCase().includes("thank-you");
    }

    function tryResolveAndCreate() {
        if (createdRef.current) return;

        const qp = queryParamsRef.current;

        //applicable for health questionnaries
        const existingToken = qp.get("token"); 

        if (existingToken) {                           
            console.info("Existing hq token detected, skipping creation", {
                token: existingToken,
            });

            createdRef.current = true;

            navigate(`/formOriginal?token=${encodeURIComponent(existingToken)}`); 

            return;
        }
        //end of hq logic

        let resolvedFormTypeId = qp.get("formTypeId");

        if (!resolvedFormTypeId && pathnameRef.current) {
            resolvedFormTypeId = getFormTypeFromPath(pathnameRef.current);
        }
        if (!resolvedFormTypeId) {
            console.debug("[Forms] tryResolveAndCreate: formTypeId not resolved yet", {
                pathname: pathnameRef.current,
                query: qp.toString()
            });
            return;
        }

        // lock only when we�re actually creating
        createdRef.current = true;

        setFormTypeId(resolvedFormTypeId);
        setTrackingData((_trackingData) => ({ ..._trackingData, formTypeId: resolvedFormTypeId! }));

        //createNewForm(parseInt(resolvedFormTypeId!, 10), queryParamsRef.current).then(() => {
        //    setTimeout(() => sendBodyHeightToParent(), 2000);
        //});
        createNewForm(
            parseInt(resolvedFormTypeId!, 10),
            queryParamsRef.current
        );

    }

    useEffect(() => {
        if (formTypeId) return;

        let attempts = 0;
        const maxAttempts = 20;
        const intervalMs = 300; //how often to run function askAgain 
        const requiredStableReads = 3; //to check if same set of parameters came back 3 times in a row

        const askAgain = () => {
            if (formTypeId || attempts >= maxAttempts || stableReadsRef.current >= requiredStableReads) {
                console.info("[Forms] Stopping asks. State:", {
                    attempts,
                    stableReads: stableReadsRef.current,
                    formTypeId
                });
                clearInterval(interval);
                tryResolveAndCreate();
                return;
            }
            // ask parent again � now broadcast to all configured origins (or '*' if none configured, preserving prior behavior)
            const origins = parentOriginsRef.current;

            if (origins.length === 0) {
                window.parent.postMessage({ type: "REQUEST_QUERY_STRING" }, "*");
                window.parent.postMessage({ type: "REQUEST_PARENT_PATHNAME" }, "*");
            } else {
                for (const o of origins) {
                    try {
                        window.parent.postMessage({ type: "REQUEST_QUERY_STRING" }, o);
                        window.parent.postMessage({ type: "REQUEST_PARENT_PATHNAME" }, o);
                    } catch (err) {
                        console.warn(`[postMessage] failed for origin "${o}":`, err);
                    }
                }
            }
            attempts++;
        };

        const interval = setInterval(askAgain, intervalMs);

        // checks whether the query string has stopped changing yet
        const stabilize = () => {
            const serializedNow = queryParamsRef.current.toString();
            if (serializedNow === lastSerializedRef.current) {
                stableReadsRef.current++;
            } else {
                lastSerializedRef.current = serializedNow;
                stableReadsRef.current = 0;
            }
            if (attempts % 5 === 0) {
                console.debug("[Forms] stabilize tick:", {
                    attempts,
                    stableReads: stableReadsRef.current,
                    currentQuery: serializedNow
                });
            }
        };
        const stabInterval = setInterval(stabilize, intervalMs);

        return () => {
            clearInterval(interval);
            clearInterval(stabInterval);
        };
    }, [formTypeId]);

    // Message Handler
    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            const incomingOrigin = normalizeOrigin(event.origin);
            const allowed = parentOriginsRef.current;

            if (allowed.length > 0 && !allowed.includes(incomingOrigin)) {
                return;
            }

            if (createdRef.current) {
                if ((event.data || {}).type !== "REQUEST_HEIGHT") {
                    return;
                }
            }

            const parentMessageData = (event.data || {}) as {
                type?: string;
                pathname?: string;
                queryString?: string;
            };

            switch (parentMessageData.type) {
                case "QUERY_STRING": {
                    const incoming = new URLSearchParams(parentMessageData.queryString || "");
                    queryParamsRef.current = mergeParams(queryParamsRef.current, incoming);

                    const hasSignal = COOKIE_PARAM_KEYS.some((k) => queryParamsRef.current.has(k));
                    if (hasSignal) {
                        tryResolveAndCreate();
                    }
                    return;
                }

                case "PARENT_PATHNAME": {
                    if (typeof parentMessageData.pathname === "string") {
                        pathnameRef.current = parentMessageData.pathname || "";
                    }
                    tryResolveAndCreate();
                    return;
                }

                case "REQUEST_HEIGHT": {
                    sendBodyHeightToParent();
                    return;
                }

                default: {
                    const origins = allowed.length ? allowed : ["*"];
                    for (const o of origins) {
                        try {
                            window.parent.postMessage({ type: "REQUEST_PARENT_PATHNAME" }, o);
                            window.parent.postMessage({ type: "REQUEST_QUERY_STRING" }, o);
                        } catch (err) {
                            console.warn(`[postMessage] failed for origin "${o}":`, err);
                        }
                    }
                    return;
                }
            }
        };

        window.addEventListener("message", handleMessage);
        window.addEventListener("resize", sendBodyHeightToParent);

        return () => {
            window.removeEventListener("message", handleMessage);
            window.removeEventListener("resize", sendBodyHeightToParent);
        };
    }, []);

    function createNewForm(formTypeId: FormTemplateTypeEnum, queryParams: URLSearchParams) {
        console.info("[Forms] createNewForm start", { formTypeId, query: queryParams.toString() });
        setLoading(true);
        return formService
            .createByType(formTypeId, params.brand)
            .then(
                (id: number) => {
                    createQuoteTracking(id, queryParams);
                    if (formTypeId === FormTemplateTypeEnum.HealthQuestionnaire) {
                        navigate(`/formOriginal/${id}`);
                    }
                    else {
                        navigate(`${id}`);
                    }
                },
                (err) => {
                    console.log("ERROR", err);
                    return handleErrorResponse(err);
                }
            )
            .finally(() => {
                setLoading(false);
            });
    }

    //function createQuoteTracking(id: number, queryParams: URLSearchParams) {

    //    const hasUtmSource = !!queryParams.get("utm_source");
    //    const fallback = hasUtmSource ? "" : "UNKNOWN";

    //    const _trackingData: IQuoteTrackingDetail = {
    //        ...trackingData,
    //        formId: id,

    //        source: queryParams.get("utm_source") || fallback,
    //        originalSource: queryParams.get("first_utm_source") || fallback,
    //        medium: queryParams.get("utm_medium") || fallback,
    //        originalMedium: queryParams.get("first_utm_medium") || fallback,
    //        campaign: queryParams.get("utm_campaign") || fallback,
    //        originalCampaign: queryParams.get("first_utm_campaign") || fallback,
    //        keyword: queryParams.get("utm_term") || fallback,
    //        originalKeyword: queryParams.get("first_utm_term") || fallback,
    //        ip: queryParams.get("handl_ip") || fallback,
    //        gClId: queryParams.get("gclid") || "",

    //        handlLandingPage: queryParams.get("handl_landing_page") || fallback,
    //        handlLandingPageBase: queryParams.get("handl_landing_page_base") || fallback,
    //        handlRef: queryParams.get("handl_ref") || fallback,
    //        handlOriginalRef: queryParams.get("handl_original_ref") || fallback,
    //        handlRefDomain: queryParams.get("handl_ref_domain") || fallback,
    //        handlUrl: queryParams.get("handl_url") || fallback,
    //        handlUrlBase: queryParams.get("handl_url_base") || fallback,
    //        handlID: queryParams.get("handlID") || "",

    //        trafficSource: queryParams.get("traffic_source") || "",
    //        originalReferringUrl: ""
    //    };

    //    setTrackingData(_trackingData);
    //    quoteTracking.post(_trackingData);
    //}

    function createQuoteTracking(id: number, queryParams: URLSearchParams) {
        const utmSource = queryParams.get("utm_source") || "";
        const utmMedium = queryParams.get("utm_medium") || "";
        const utmCampaign = queryParams.get("utm_campaign") || "";

        const gclid = queryParams.get("gclid") || "";
        const fbclid = queryParams.get("fbclid") || "";
        const fbc = queryParams.get("_fbc") || "";
        const fbp = queryParams.get("_fbp") || "";

        const hasMetaClick = !!fbclid || !!fbc;
        const hasGoogleClick = !!gclid;

        // Only infer when UTMs are missing
        const inferredSource = utmSource || (hasMetaClick ? "facebook" : hasGoogleClick ? "google" : "UNKNOWN");

        const inferredMedium = utmMedium || (hasMetaClick ? "paid_social" : hasGoogleClick ? "cpc" : "UNKNOWN");

        const fallback = inferredSource ? "" : "UNKNOWN"; // optional, but keeps blanks for known cases

        const _trackingData: IQuoteTrackingDetail = {
            ...trackingData,
            formId: id,

            source: inferredSource,
            originalSource: queryParams.get("first_utm_source") || inferredSource,
            medium: inferredMedium,
            originalMedium: queryParams.get("first_utm_medium") || inferredMedium,
            campaign: utmCampaign || (hasMetaClick ? "meta" : hasGoogleClick ? "google_ads" : "UNKNOWN"),
            originalCampaign: queryParams.get("first_utm_campaign") || utmCampaign || "",
            keyword: queryParams.get("utm_term") || queryParams.get("first_utm_term") || "",
            originalKeyword: queryParams.get("first_utm_term") || "",

            ip: queryParams.get("handl_ip") || "",
            gClId: gclid,

            handlLandingPage: queryParams.get("handl_landing_page") || "",
            handlLandingPageBase: queryParams.get("handl_landing_page_base") || "",
            handlRef: queryParams.get("handl_ref") || "",
            handlOriginalRef: queryParams.get("handl_original_ref") || "",
            handlRefDomain: queryParams.get("handl_ref_domain") || "",
            handlUrl: queryParams.get("handl_url") || "",
            handlUrlBase: queryParams.get("handl_url_base") || "",
            handlID: queryParams.get("handlID") || "",

            trafficSource: queryParams.get("traffic_source") || (hasMetaClick ? "meta" : hasGoogleClick ? "google_ads" : ""),
            originalReferringUrl: "",

            metaFbc: fbc, // a cookie version of fbclid that Meta uses to persist ad clicks
            metaFbp: fbp, //a browser identifier set by the Meta Pixel
            metaFbclid: fbclid, //a click ID added by Meta to the URL when someone clicks a Facebook or Instagram ad
        };

        setTrackingData(_trackingData);
        quoteTracking.post(_trackingData);
    }


    return (
        <div aria-busy={isLoading || undefined}>
            <FormControl fullWidth variant="outlined">
            </FormControl>
        </div>
    );
}

export default Forms;
